/**
 * On-demand recovery hook for missing email bodies.
 *
 * Called when the per-thread fast-poll window in `useEmailThreadDB` exhausts
 * without `email_bodies.body_html` landing. Invokes the `fetch_message_body`
 * action on the correspondence-api facade, which:
 *   1. Resolves the owning account from `email_metadata.account_id`
 *   2. Refreshes the provider token
 *   3. Fetches the message via the provider adapter
 *   4. Upserts `email_bodies` via the shared `store-bodies` module — or, for
 *      an account with `store_bodies = false`, returns the body in the result
 *      (`status: 'fetched'`) without storing it
 *
 * On success, invalidates the email-thread cache so the slow-polling query
 * picks up the newly stored body on its next tick — typically within
 * milliseconds because TanStack Query refetches the active query
 * immediately on invalidation.
 *
 * Cache invalidation strategy (conditional):
 *   - On `status === 'stored'` AND the caller passed `threadId`: invalidate
 *     just `emailThreadDbKeys.thread(orgId, threadId)`. The body landed for
 *     a known thread; only that thread's polling query needs to refetch.
 *   - On any other status (`not_found`, `failed`, `skipped`, `transient_error`)
 *     OR when `threadId` is absent: invalidate the broad
 *     `emailThreadDbKeys.all` prefix. We still want polling to refresh so
 *     the UI can flip out of the loading state and pick up a terminal row
 *     (e.g. backfill landed the body between our retry and the next poll
 *     tick); the broad prefix is the safe default when we lack a tighter
 *     key, and TanStack's `staleTime` blunts the cost of refetching idle
 *     threads. See `.claude/rules/correspondence-api-transition.md` rule 24
 *     for the underlying key-prefix discipline.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { invokeCorrespondenceApi } from '@/hooks/use-correspondence-api';
import { emailThreadDbKeys } from '@/hooks/use-email-thread-db';
import { getLogoutEpoch } from '@/lib/logout-teardown-signal';

import type {
  FetchedMessageBody,
  FetchMessageBodyInput,
  FetchMessageBodyResult,
} from '@/lib/correspondence/contracts';

/**
 * Bodies the server returned without storing them (`status: 'fetched'`: the
 * account opted out of content storage). Memory only: the root is excluded
 * from `persistQueryClient` in `lib/query-persistence.ts`, and
 * `queryClient.clear()` drops it on sign-out. Keyed by `email_metadata.id`
 * so every renderer of the same message shares one fetch.
 */
export const fetchedEmailBodyKeys = {
  all: ['fetched-email-body'] as const,
  body: (emailMetadataId: string | undefined) =>
    [...fetchedEmailBodyKeys.all, emailMetadataId] as const,
};

// Bumped by every eviction; a fetch that started under an older value lands
// after its access may have been revoked, so its body is dropped. Sign-out and
// account replacement are covered by the logout epoch instead.
const evictionEpochs = new WeakMap<QueryClient, number>();

function evictionEpoch(queryClient: QueryClient): number {
  return evictionEpochs.get(queryClient) ?? 0;
}

/**
 * Drop every fetched body, including from renderers still mounted, and discard
 * the result of any fetch already in flight. A reset, not a remove: the read
 * never refetches, so a removed query leaves a mounted observer holding its
 * last body.
 */
export async function evictFetchedEmailBodies(queryClient: QueryClient): Promise<void> {
  evictionEpochs.set(queryClient, evictionEpoch(queryClient) + 1);
  await queryClient.resetQueries({ queryKey: fetchedEmailBodyKeys.all });
}

/** The body a `fetched` result left for this message, if any. Never fetches. */
export function useFetchedEmailBody(
  emailMetadataId: string | undefined,
): FetchedMessageBody | undefined {
  const { data } = useQuery<FetchedMessageBody | null>({
    queryKey: fetchedEmailBodyKeys.body(emailMetadataId),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });
  return data ?? undefined;
}

export interface UseFetchEmailBodyOptions {
  organizationId: string | undefined;
  /**
   * Caller's open thread id. Used to issue a tighter cache invalidation
   * when present. Omit to fall back to the broad `emailThreadDbKeys.all`
   * prefix (still correct, just refetches more threads).
   */
  threadId?: string;
}

export interface FetchEmailBodyVariables {
  emailMetadataId: string;
}

interface FetchContext {
  epoch: number;
  logoutEpoch: number;
}

/** Nothing has evicted fetched bodies or torn the session down since the fetch began. */
function isCurrent(queryClient: QueryClient, context: FetchContext | undefined): boolean {
  return context?.epoch === evictionEpoch(queryClient) && context.logoutEpoch === getLogoutEpoch();
}

export type UseFetchEmailBodyResult = UseMutationResult<
  FetchMessageBodyResult,
  Error,
  FetchEmailBodyVariables,
  FetchContext
>;

export function useFetchEmailBody(options: UseFetchEmailBodyOptions): UseFetchEmailBodyResult {
  const queryClient = useQueryClient();
  const { organizationId, threadId } = options;

  return useMutation<FetchMessageBodyResult, Error, FetchEmailBodyVariables, FetchContext>({
    mutationFn: async ({ emailMetadataId }) => {
      if (!organizationId) {
        throw new Error('organizationId is required to fetch an email body');
      }
      const input: FetchMessageBodyInput = {
        action: 'fetch_message_body',
        organizationId,
        emailMetadataId,
      };
      return await invokeCorrespondenceApi('fetch_message_body', input);
    },
    onMutate: () => ({ epoch: evictionEpoch(queryClient), logoutEpoch: getLogoutEpoch() }),
    onSuccess: async (result, { emailMetadataId }, context) => {
      // The body came back in the response and was written nowhere, so there is
      // no row for a refetch to find.
      if (result.status === 'fetched') {
        if (isCurrent(queryClient, context)) {
          queryClient.setQueryData(fetchedEmailBodyKeys.body(emailMetadataId), result.body);
        }
        return;
      }
      if (result.status !== 'stored') {
        // For terminal-but-empty outcomes (`not_found`, `failed`, `skipped`)
        // we still want the polling query to refresh so the UI can flip to
        // the `EmailExtractionFailed` state. Transient errors also benefit
        // from a re-poll in case backfill already filled the row.
        await queryClient.invalidateQueries({ queryKey: emailThreadDbKeys.all });
        return;
      }
      // Body landed — invalidate the thread query so the slow-polling tick
      // doesn't sit on a 30 s staleTime before the body shows up.
      if (threadId && organizationId) {
        await queryClient.invalidateQueries({
          queryKey: emailThreadDbKeys.thread(organizationId, threadId),
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: emailThreadDbKeys.all });
    },
  });
}
